// Real checkout funnel events (R-aug-02).
//
// "Started checkout" used to mean "has any Purchase row", which was true for
// almost every paid or previously-paid user and therefore measured nothing.
// These events are emitted where the buyer actually is: at the payment step,
// per processor.
//
// Definition of started: the payment UI was presented (Valmar card form
// mounted, Whop embed mounted, or the native store sheet opened). Every started
// attempt ends in exactly one of completed / failed / abandoned, so the funnel
// arithmetic holds.

export const CHECKOUT_EVENT = {
  STARTED: 'started',
  ABANDONED: 'abandoned',
  FAILED: 'failed',
};

export const CHECKOUT_ABANDON_REASON = {
  CLOSED: 'closed',
  NAVIGATED_AWAY: 'navigated_away',
  // Our own Valmar → Whop fallback, not the buyer giving up. Named so the
  // analysis can subtract it instead of reading it as lost intent.
  PROVIDER_SWITCH: 'provider_switch',
};

/**
 * Which terminal event a native store purchase result deserves.
 *
 * Charged-but-unfulfilled and store-pending purchases remain pending; only
 * durable backend fulfillment counts as a completed checkout.
 *
 * @returns {'completed'|'abandoned'|'failed'|'pending'}
 */
export function nativeIapOutcome(result) {
  if (result?.success) return 'completed';
  if (result?.cancelled) return 'abandoned';
  if (result?.pending === false) return 'failed';
  if (result?.charged || result?.pending || result?.pendingValidation) return 'pending';
  return 'failed';
}

/**
 * Tracks one checkout attempt at a time and emits exactly one terminal event
 * for it. Pure and dependency-free so it can be unit tested without React.
 *
 * @param {(payload: {event: string, processor: string, checkoutType: string, reason?: string}) => void} emit
 */
export function createCheckoutAttemptTracker(emit) {
  let attempt = null;

  const send = (event, reason) => {
    if (!attempt) return;
    const { processor, checkoutType } = attempt;
    attempt = null;
    emit({ event, processor, checkoutType, ...(reason ? { reason } : {}) });
  };

  return {
    /**
     * Payment UI presented. Remounting the same processor's form (Whop mints a
     * fresh session after a decline) is the same funnel entry, so it does not
     * emit a second `started`. Switching processors closes the old attempt.
     */
    start({ processor, checkoutType }) {
      if (!processor || !checkoutType) return;
      if (attempt) {
        if (attempt.processor === processor && attempt.checkoutType === checkoutType) {
          return;
        }
        send(CHECKOUT_EVENT.ABANDONED, CHECKOUT_ABANDON_REASON.PROVIDER_SWITCH);
      }
      attempt = { processor, checkoutType };
      emit({ event: CHECKOUT_EVENT.STARTED, processor, checkoutType });
    },

    /** Paid. The Purchase record already measures this, so nothing is emitted. */
    complete() {
      attempt = null;
    },

    /** Declined, 3DS failure, or any processor-side error. */
    fail(reason) {
      send(CHECKOUT_EVENT.FAILED, typeof reason === 'string' ? reason : undefined);
    },

    /** Left the payment step without paying. */
    abandon(reason = CHECKOUT_ABANDON_REASON.CLOSED) {
      send(CHECKOUT_EVENT.ABANDONED, reason);
    },

    /**
     * The open attempt, or null. Completion emits nothing, so a caller that
     * needs the processor/type of the attempt it is about to complete has to
     * read them off here first.
     */
    peek() {
      return attempt ? { ...attempt } : null;
    },
  };
}
